// Route guard for `/capture/sites/:siteId/capture`.
//
// PRD §6.5 / AGENTS.md §Field additions limit field staff to leaf-level
// capture and reference-data changes. The companion PR (#453) removed
// the "Site-level capture" entry-point button from the buildings list
// because the product team decided field staff should not create
// site-scoped capture sessions. Hiding the CTA alone is not enough —
// the route is still reachable via shared/bookmarked/manual URLs and
// `<SiteCaptureScreen>` will happily POST `/capture/sessions` for a
// field_staff caller (see Codex review on PR #453). This guard closes
// that hole at the route boundary.
//
// Branching rules (mirrored from RequireAdmin so the three guards
// stay behaviourally consistent on loading / error / unauth states):
//   1. 'loading'              → BrandedSplash — no navigation yet.
//   2. 'unauthenticated'      → kick out to /auth/login preserving
//                               the current URL via redirect_to. The
//                               outer <RequireAuth> already does this
//                               for the /capture tree, but mirroring
//                               here keeps the guard standalone-safe
//                               if it is ever lifted out of the
//                               wrapping tree.
//   3. 'error'                → surface the error; do NOT redirect to
//                               login (a 5xx on /me is not "sign in
//                               again").
//   4. 'authenticated' && role === 'field_staff'
//                             → redirect to /capture/sites/:siteId so
//                               the field staff member lands back on
//                               the buildings list (the only
//                               supported entry point for them today).
//                               `replace` so the back button does not
//                               bounce them into the same redirect.
//   5. 'authenticated' && role === 'admin'
//                             → render children. Admins see the
//                               read-only Admin Preview banner inside
//                               <SiteCaptureScreen> (buildspec 20-ui
//                               §1.1a) and never POST /capture/sessions
//                               from this screen.
//
// Tests exercise the field_staff and admin branches via
// `apps/frontend/src/app/router.test.tsx` (parameterised describe
// for /capture/sites/:siteId/capture) and the dedicated guard-level
// test alongside this file. The component-level tests in
// `apps/frontend/src/capture/pages/site-capture-screen.test.tsx`
// continue to render <SiteCaptureScreen> directly without this guard
// so behavioural assertions there stay intact (ADR-009 — test
// integrity).

import { useEffect, type ReactNode } from 'react'
import { Navigate, useParams } from 'react-router'

import { buildLoginUrl, navigateTo } from '../hooks/api'
import { BrandedSplash } from '../ui/branded-splash'

import { useAuth } from './auth-provider'

export interface RequireAdminSiteCaptureProps {
  children: ReactNode
}

export function RequireAdminSiteCapture({
  children,
}: RequireAdminSiteCaptureProps): ReactNode {
  const { status, user, error } = useAuth()
  const { siteId = '' } = useParams<{ siteId: string }>()

  useEffect(() => {
    if (status === 'unauthenticated' && typeof window !== 'undefined') {
      const here = `${window.location.pathname}${window.location.search}`
      navigateTo(buildLoginUrl(here))
    }
  }, [status])

  if (status === 'loading') {
    return <BrandedSplash message="Loading session…" />
  }

  if (status === 'unauthenticated') {
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex min-h-screen items-center justify-center p-6 text-sm text-gray-600"
      >
        Redirecting to sign in…
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div
        role="alert"
        className="flex min-h-screen items-center justify-center p-6 text-sm text-red-700"
      >
        <div className="max-w-md space-y-2 text-center">
          <h1 className="text-lg font-semibold">Could not verify session</h1>
          <p>{error?.message ?? 'Unknown error contacting the server.'}</p>
        </div>
      </div>
    )
  }

  if (user === undefined || user.role === 'field_staff') {
    // Bounce field_staff back to the buildings list. The siteId param
    // is URL-encoded the same way SiteDetailPage produces its outbound
    // links so a deep-link round-trip lands in a known-good location.
    const target = `/capture/sites/${encodeURIComponent(siteId)}`
    return <Navigate to={target} replace />
  }

  return <>{children}</>
}
